import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { type RiskLevelCode, getRiskBadgeClassName } from "@/lib/risk-level";
import {
  Search,
  Filter,
  X,
  Download,
  Info,
  TrendingUp,
  Phone,
  Mail,
  FileText,
  Target,
  ArrowRight,
  Scale,
  CreditCard,
  UserCheck,
  Settings,
} from "lucide-react";
import { HelpSupportButton } from "@/components/help-support";
import { type CustomerAgeingData, type AgeingAnalysisFilters, type SmartActionsRecommendation, type AgeingBandConfig } from "@/types/ageing-analysis";
import { generateSmartActions } from "@/lib/mock-data/ageing-analysis-mock-data";
import { AdvancedFiltersSheet } from "@/components/collections/advanced-filters-sheet";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { DataTable, type DataTableColumn } from "@/components/shared/data-table";
import { CustomerDetailsDrawer } from "@/components/collections/customer-details-drawer";
import { SmartActionsDrawer } from "@/components/collections/smart-actions-drawer";
import { LogCallDialog, SendEmailDialog, CreatePromiseDialog, AssignClerkDialog } from "@/components/collections/ageing-action-dialogs";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { toast } from "sonner";
import { CurrencyToggle, type CurrencyMode } from "@/components/shared/currency-toggle";
import { useCompany } from "@/contexts/CompanyContext";
import { useAgeingBands, DEFAULT_AGEING_BANDS } from "@/hooks/useAgeingBands";
import { useAgeingAnalysisPageData } from "@/hooks/useAgeingAnalysisGraphQL";
import { useMode } from "@/providers/ModeProvider";
import { useGeneralSettings } from "@/hooks/useGeneralSettings";


function buildDefaultColumns(bands: AgeingBandConfig[], sortKeyLabels: Record<string, string>): ColumnDefinition[] {
  const staticBefore: ColumnDefinition[] = [
    { id: 'accountNumber', label: 'Account #', visible: true, locked: true, order: 0 },
    { id: 'customerName', label: 'Name', visible: true, locked: true, order: 1 },
    { id: 'currency', label: 'Currency', visible: true, order: 2 },
    { id: 'creditLimit', label: 'Credit Limit', visible: true, order: 3 },
    { id: 'totalOutstanding', label: 'Outstanding', visible: true, order: 4 },
  ];

  const bandColumns: ColumnDefinition[] = bands.map((band, i) => ({
    id: `band_${band.sequence}`,
    label: band.bandLabel,
    visible: true,
    order: 5 + i,
  }));

  const staticAfter: ColumnDefinition[] = [
    { id: 'terms', label: 'Terms', visible: true, order: 5 + bands.length },
    { id: 'riskLevel', label: 'Risk Level', visible: true, order: 6 + bands.length },
    { id: 'collectionStrategy', label: 'Collection Strategy', visible: true, order: 7 + bands.length },
    { id: 'lastPayment', label: 'Last Payment', visible: false, order: 8 + bands.length },
    { id: 'contactEmail', label: 'Contact Email', visible: false, order: 9 + bands.length },
    { id: 'contactPhone', label: 'Contact Phone', visible: false, order: 10 + bands.length },
    { id: 'creditController', label: 'Credit Controller', visible: false, order: 11 + bands.length },
    { id: 'accountManager', label: 'Account Manager', visible: false, order: 12 + bands.length },
    { id: 'userSortKey1', label: sortKeyLabels.userSortKey1, visible: false, order: 13 + bands.length },
    { id: 'userSortKey2', label: sortKeyLabels.userSortKey2, visible: false, order: 14 + bands.length },
    { id: 'userSortKey3', label: sortKeyLabels.userSortKey3, visible: false, order: 15 + bands.length },
    { id: 'userSortKey4', label: sortKeyLabels.userSortKey4, visible: false, order: 16 + bands.length },
  ];

  return [...staticBefore, ...bandColumns, ...staticAfter];
}

function buildTableColumns(bands: AgeingBandConfig[], visibleColumnIds: Set<string>, sortKeyLabels: Record<string, string>): DataTableColumn[] {
  const allCols: DataTableColumn[] = [
    { id: 'accountNumber', label: 'Account #', sortKey: 'accountNumber', defaultWidth: 130 },
    { id: 'customerName', label: 'Name', sortKey: 'customerName', defaultWidth: 200 },
    { id: 'currency', label: 'Currency', sortKey: 'currency', defaultWidth: 90 },
    { id: 'creditLimit', label: 'Credit Limit', sortKey: 'creditLimit', align: 'right', defaultWidth: 130 },
    { id: 'totalOutstanding', label: 'Outstanding', sortKey: 'totalOutstanding', align: 'right', defaultWidth: 130 },
  ];

  for (const band of bands) {
    allCols.push({
      id: `band_${band.sequence}`,
      label: band.bandLabel,
      align: 'right',
      defaultWidth: 130,
    });
  }

  allCols.push(
    { id: 'terms', label: 'Terms', sortKey: 'terms', defaultWidth: 100 },
    { id: 'riskLevel', label: 'Risk Level', sortKey: 'riskLevel', defaultWidth: 110 },
    { id: 'collectionStrategy', label: 'Collection Strategy', sortKey: 'collectionStrategy', defaultWidth: 170 },
    { id: 'lastPayment', label: 'Last Payment', sortKey: 'lastPayment', defaultWidth: 120 },
    { id: 'contactEmail', label: 'Contact Email', defaultWidth: 200 },
    { id: 'contactPhone', label: 'Contact Phone', defaultWidth: 140 },
    { id: 'creditController', label: 'Credit Controller', sortKey: 'creditController', defaultWidth: 160 },
    { id: 'accountManager', label: 'Account Manager', sortKey: 'accountManager', defaultWidth: 160 },
    { id: 'userSortKey1', label: sortKeyLabels.userSortKey1, sortKey: 'userSortKey1', defaultWidth: 140 },
    { id: 'userSortKey2', label: sortKeyLabels.userSortKey2, sortKey: 'userSortKey2', defaultWidth: 140 },
    { id: 'userSortKey3', label: sortKeyLabels.userSortKey3, sortKey: 'userSortKey3', defaultWidth: 140 },
    { id: 'userSortKey4', label: sortKeyLabels.userSortKey4, sortKey: 'userSortKey4', defaultWidth: 140 },
  );

  return allCols.filter((col) => visibleColumnIds.has(col.id));
}

export default function AgeingAnalysisPage() {
  const { selectedCompany } = useCompany();
  const { isDemo } = useMode();
  const navigate = useNavigate();
  const baseCurrency = selectedCompany?.currency ?? "GBP";
  const { data: generalSettings } = useGeneralSettings();

  // Override user sort key labels with organisation-level custom labels from settings
  const userSortKeyLabelMap: Record<string, string> = useMemo(() => ({
    userSortKey1: generalSettings?.categoryKeys?.userSortKey1Label ?? 'User Sort Key 1',
    userSortKey2: generalSettings?.categoryKeys?.userSortKey2Label ?? 'User Sort Key 2',
    userSortKey3: generalSettings?.categoryKeys?.userSortKey3Label ?? 'User Sort Key 3',
    userSortKey4: generalSettings?.categoryKeys?.userSortKey4Label ?? 'User Sort Key 4',
  }), [generalSettings]);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<string>("accountNumber");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>("customer");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [filters, setFilters] = useState<AgeingAnalysisFilters>({
    searchQuery: "",
    customerIds: [],
    riskLevel: [],
    currency: [],
    terms: [],
    creditController: [],
    accountManager: [],
    daysOverdueMin: null,
    daysOverdueMax: null,
    outstandingMin: null,
    outstandingMax: null,
    creditLimitMin: null,
    creditLimitMax: null,
    lastPaymentFrom: null,
    lastPaymentTo: null,
    collectionStrategy: [],
    userSortKey1: [],
    userSortKey2: [],
    userSortKey3: [],
    userSortKey4: [],
  });

  // UI states
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [smartActionsOpen, setSmartActionsOpen] = useState(false);
  const [customerDetailsOpen, setCustomerDetailsOpen] = useState(false);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [createPromiseOpen, setCreatePromiseOpen] = useState(false);
  const [assignClerkOpen, setAssignClerkOpen] = useState(false);
  const [activeCustomer, setActiveCustomer] = useState<CustomerAgeingData | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerAgeingData | null>(null);
  const [smartRecommendation, setSmartRecommendation] = useState<SmartActionsRecommendation | null>(null);

  const [tempFilters, setTempFilters] = useState<AgeingAnalysisFilters>(filters);

  // Fetch ageing bands
  const { data: ageingBands } = useAgeingBands();
  const bands = ageingBands ?? DEFAULT_AGEING_BANDS;

  // Build dynamic default columns from ageing bands
  const defaultColumns = useMemo(() => buildDefaultColumns(bands, userSortKeyLabelMap), [bands, userSortKeyLabelMap]);

  // Column preferences
  const { columns, visibleColumns: visibleColumnsArray, savePreferences, resetToDefaults } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.AgeingAnalysis, defaultColumns);

  // Override user sort key labels with organisation-level custom labels from settings
  const columnsWithLabels = useMemo(() =>
    columns.map((col) =>
      col.id in userSortKeyLabelMap ? { ...col, label: userSortKeyLabelMap[col.id] } : col
    ), [columns, userSortKeyLabelMap]);

  const visibleColumnIds = useMemo(() => {
    const set = new Set<string>();
    for (const col of visibleColumnsArray) {
      set.add(col.id);
    }
    return set;
  }, [visibleColumnsArray]);

  // Build table columns from bands and visible preferences
  const tableColumns = useMemo(() => buildTableColumns(bands, visibleColumnIds, userSortKeyLabelMap), [bands, visibleColumnIds, userSortKeyLabelMap]);

  // Fetch data using the mode-aware hook
  const { customers, pagination, isLoading } = useAgeingAnalysisPageData({
    page: currentPage,
    pageSize,
    search: searchQuery || undefined,
    sortBy: sortField,
    sortOrder: sortDirection,
    filters,
  });

  // Apply client-side filtering.
  // In demo mode: all filters are applied client-side.
  // In live mode: most filters are handled server-side via GraphQL,
  //   but some filters (daysOverdue) don't have
  //   backend equivalents so we apply them client-side after the server results.
  const displayCustomers = useMemo(() => {
    let filtered = customers;

    if (isDemo) {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (c) =>
            c.customerName.toLowerCase().includes(query) ||
            c.accountNumber.toLowerCase().includes(query) ||
            c.contact.toLowerCase().includes(query),
        );
      }

      if (filters.customerIds.length > 0) filtered = filtered.filter((c) => filters.customerIds.includes(c.id));
      if (filters.riskLevel.length > 0) filtered = filtered.filter((c) => filters.riskLevel.includes(c.riskLevel));
      if (filters.currency.length > 0) filtered = filtered.filter((c) => filters.currency.includes(c.currency));
      if (filters.outstandingMin !== null) filtered = filtered.filter((c) => c.totalOutstanding >= filters.outstandingMin!);
      if (filters.outstandingMax !== null) filtered = filtered.filter((c) => c.totalOutstanding <= filters.outstandingMax!);
      if (filters.collectionStrategy.length > 0) filtered = filtered.filter((c) => filters.collectionStrategy.includes(c.collectionStrategy));
      if (filters.terms.length > 0) filtered = filtered.filter((c) => filters.terms.includes(c.terms ?? ""));
      if (filters.creditController.length > 0) filtered = filtered.filter((c) => filters.creditController.includes(c.creditControllerName ?? ""));
      if (filters.accountManager.length > 0) filtered = filtered.filter((c) => filters.accountManager.includes(c.accountManagerName ?? ""));
      if (filters.creditLimitMin !== null) filtered = filtered.filter((c) => c.creditLimit >= filters.creditLimitMin!);
      if (filters.creditLimitMax !== null) filtered = filtered.filter((c) => c.creditLimit <= filters.creditLimitMax!);
      if (filters.lastPaymentFrom !== null) filtered = filtered.filter((c) => !c.lastPayment || new Date(c.lastPayment) >= new Date(filters.lastPaymentFrom!));
      if (filters.lastPaymentTo !== null) filtered = filtered.filter((c) => !c.lastPayment || new Date(c.lastPayment) <= new Date(filters.lastPaymentTo!));
      if (filters.userSortKey1.length > 0) filtered = filtered.filter((c) => filters.userSortKey1.includes(c.userSortKey1 ?? ""));
      if (filters.userSortKey2.length > 0) filtered = filtered.filter((c) => filters.userSortKey2.includes(c.userSortKey2 ?? ""));
      if (filters.userSortKey3.length > 0) filtered = filtered.filter((c) => filters.userSortKey3.includes(c.userSortKey3 ?? ""));
      if (filters.userSortKey4.length > 0) filtered = filtered.filter((c) => filters.userSortKey4.includes(c.userSortKey4 ?? ""));
    }

    // These filters are applied client-side in both demo and live modes
    // because they don't have backend equivalents
    if (filters.daysOverdueMin !== null) filtered = filtered.filter((c) => c.worstDaysOverdue >= filters.daysOverdueMin!);
    if (filters.daysOverdueMax !== null) filtered = filtered.filter((c) => c.worstDaysOverdue <= filters.daysOverdueMax!);
    return filtered;
  }, [customers, isDemo, searchQuery, filters]);

  // Determine if a single currency is selected so KPI cards can show
  // amounts in that currency with the correct symbol.
  const kpiCurrency = filters.currency.length === 1 ? filters.currency[0] : baseCurrency;
  const useCustomerCurrency = filters.currency.length === 1;

  // Pick the correct band data for a customer based on the currency toggle.
  // Base-currency customers are never affected by the toggle – they always
  // use baseBands / baseTotalOutstanding so their values are guaranteed.
  const pickBandData = useCallback((customer: CustomerAgeingData) => {
    if (currencyMode === "base" || customer.currency === customer.homeCurrencyCode) {
      return customer.baseBands;
    }
    return customer.bands;
  }, [currencyMode]);

  const pickOutstanding = useCallback((customer: CustomerAgeingData) => {
    if (currencyMode === "base" || customer.currency === customer.homeCurrencyCode) {
      return customer.baseTotalOutstanding;
    }
    return customer.totalOutstanding;
  }, [currencyMode]);

  // Compute KPI band totals from displayCustomers so they always reflect
  // the current grid filters across all pages.
  const bandMetrics = useMemo(() => {
    const metrics: Record<number, number> = {};
    for (const band of bands) {
      metrics[band.sequence] = 0;
    }

    for (const customer of displayCustomers) {
      const bandData = useCustomerCurrency
        ? (customer.currency === customer.homeCurrencyCode ? customer.baseBands : customer.bands)
        : customer.baseBands;
      for (const band of bands) {
        metrics[band.sequence] += bandData[band.sequence] ?? 0;
      }
    }

    return metrics;
  }, [displayCustomers, bands, useCustomerCurrency]);

  // Pagination — always client-side after pivoting.
  // In live mode the hook fetches all rows from the per-band view and pivots
  // them into unique customers, so we paginate the resulting array here.
  const totalRecords = displayCustomers.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return displayCustomers.slice(start, start + pageSize);
  }, [displayCustomers, currentPage, pageSize]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };


  const applyFilters = () => {
    setFilters(tempFilters);
    setFiltersSheetOpen(false);
    setCurrentPage(1);
    toast.success("Filters applied");
  };

  const resetFilters = () => {
    const emptyFilters: AgeingAnalysisFilters = {
      searchQuery: "",
      customerIds: [],
      riskLevel: [],
      currency: [],
      terms: [],
      creditController: [],
      accountManager: [],
      daysOverdueMin: null,
      daysOverdueMax: null,
      outstandingMin: null,
      outstandingMax: null,
      creditLimitMin: null,
      creditLimitMax: null,
      lastPaymentFrom: null,
      lastPaymentTo: null,
      collectionStrategy: [],
      userSortKey1: [],
      userSortKey2: [],
      userSortKey3: [],
      userSortKey4: [],
    };
    setTempFilters(emptyFilters);
    setFilters(emptyFilters);
    setSearchQuery("");
    setCurrentPage(1);
    toast.info("Filters cleared");
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.customerIds.length > 0) count++;
    if (filters.riskLevel.length > 0) count++;
    if (filters.currency.length > 0) count++;
    if (filters.terms.length > 0) count++;
    if (filters.creditController.length > 0) count++;
    if (filters.accountManager.length > 0) count++;
    if (filters.daysOverdueMin !== null || filters.daysOverdueMax !== null) count++;
    if (filters.outstandingMin !== null || filters.outstandingMax !== null) count++;
    if (filters.creditLimitMin !== null || filters.creditLimitMax !== null) count++;
    if (filters.lastPaymentFrom !== null || filters.lastPaymentTo !== null) count++;
    if (filters.collectionStrategy.length > 0) count++;
    if (filters.userSortKey1.length > 0) count++;
    if (filters.userSortKey2.length > 0) count++;
    if (filters.userSortKey3.length > 0) count++;
    if (filters.userSortKey4.length > 0) count++;
    return count;
  }, [filters]);

  const removeFilter = (filterType: string) => {
    const newFilters = { ...filters };
    switch (filterType) {
      case "customer": newFilters.customerIds = []; break;
      case "riskLevel": newFilters.riskLevel = []; break;
      case "currency": newFilters.currency = []; break;
      case "daysOverdue": newFilters.daysOverdueMin = null; newFilters.daysOverdueMax = null; break;
      case "outstanding": newFilters.outstandingMin = null; newFilters.outstandingMax = null; break;
      case "terms": newFilters.terms = []; break;
      case "creditController": newFilters.creditController = []; break;
      case "accountManager": newFilters.accountManager = []; break;
      case "creditLimit": newFilters.creditLimitMin = null; newFilters.creditLimitMax = null; break;
      case "lastPayment": newFilters.lastPaymentFrom = null; newFilters.lastPaymentTo = null; break;
      case "strategy": newFilters.collectionStrategy = []; break;
      case "userSortKey1": newFilters.userSortKey1 = []; break;
      case "userSortKey2": newFilters.userSortKey2 = []; break;
      case "userSortKey3": newFilters.userSortKey3 = []; break;
      case "userSortKey4": newFilters.userSortKey4 = []; break;
    }
    setFilters(newFilters);
    setTempFilters(newFilters);
  };

  const handleViewCustomer = (customer: CustomerAgeingData) => {
    setSelectedCustomer(customer);
    setSmartRecommendation(generateSmartActions(customer));
    setCustomerDetailsOpen(true);
  };

  const handleExport = (format: string) => {
    if (format !== "CSV") return;

    const headers = tableColumns.map((col) => col.label);
    const rows = displayCustomers.map((customer) => {
      return tableColumns.map((col) => {
        if (col.id.startsWith("band_")) {
          const seq = Number(col.id.replace("band_", ""));
          const bandData = pickBandData(customer);
          return String(bandData[seq] ?? 0);
        }
        switch (col.id) {
          case "accountNumber": return customer.accountNumber;
          case "customerName": return customer.customerName;
          case "currency": return customer.currency;
          case "creditLimit": return String(customer.creditLimit);
          case "totalOutstanding": {
            return String(pickOutstanding(customer));
          }
          case "terms": return customer.terms || "";
          case "riskLevel": return customer.riskLevel;
          case "collectionStrategy": return customer.collectionStrategy || "";
          case "lastPayment": return customer.lastPayment ? new Date(customer.lastPayment).toLocaleDateString("en-GB") : "";
          case "contactEmail": return customer.contact || "";
          case "contactPhone": return customer.phone || "";
          case "creditController": return customer.creditControllerName || "";
          case "accountManager": return customer.accountManagerName || "";
          case "userSortKey1": return customer.userSortKey1 || "";
          case "userSortKey2": return customer.userSortKey2 || "";
          case "userSortKey3": return customer.userSortKey3 || "";
          case "userSortKey4": return customer.userSortKey4 || "";
          default: return "";
        }
      });
    });

    const escapeCsvField = (field: string) => {
      if (field.includes(",") || field.includes('"') || field.includes("\n")) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    };

    const csvContent = [
      headers.map(escapeCsvField).join(","),
      ...rows.map((row) => row.map(escapeCsvField).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ageing-analysis-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("CSV exported successfully");
  };

  const handleExecuteAction = (actionTitle: string) => {
    toast.success(`Initiating: ${actionTitle}`);
  };

  const formatCurrency = useCallback((amount: number, currencyCode = baseCurrency) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, [baseCurrency]);

  const getRiskBadgeColor = (risk: string) => getRiskBadgeClassName(risk as RiskLevelCode);

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case "critical": return "bg-red-600 text-white";
      case "high": return "bg-orange-600 text-white";
      case "medium": return "bg-yellow-600 text-white";
      case "low": return "bg-blue-600 text-white";
      default: return "";
    }
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case "call": return <Phone className="h-4 w-4" />;
      case "email": return <Mail className="h-4 w-4" />;
      case "letter": return <FileText className="h-4 w-4" />;
      case "visit": return <UserCheck className="h-4 w-4" />;
      case "legal": return <Scale className="h-4 w-4" />;
      case "payment_plan": return <CreditCard className="h-4 w-4" />;
      case "escalate": return <TrendingUp className="h-4 w-4" />;
      case "negotiate": return <Target className="h-4 w-4" />;
      default: return <ArrowRight className="h-4 w-4" />;
    }
  };

  const getActionButtonText = (actionType: string) => {
    switch (actionType) {
      case "call": return "Make Call";
      case "email": return "Send Email";
      case "letter": return "Generate Letter";
      case "visit": return "Schedule Visit";
      case "legal": return "Initiate Legal Action";
      case "payment_plan": return "Create Payment Plan";
      case "escalate": return "Escalate Case";
      case "negotiate": return "Start Negotiation";
      default: return "Take Action";
    }
  };

  const renderCell = useCallback((columnId: string, customer: CustomerAgeingData) => {
    if (columnId.startsWith("band_")) {
      const seq = Number(columnId.replace("band_", ""));
      const bandData = pickBandData(customer);
      const amount = bandData[seq] ?? 0;
      const displayCurrency = currencyMode === "base" ? (customer.homeCurrencyCode ?? baseCurrency) : customer.currency;
      return <span className="font-medium">{formatCurrency(amount, displayCurrency)}</span>;
    }

    switch (columnId) {
      case "accountNumber":
        return (
          <button onClick={() => handleViewCustomer(customer)} className="text-primary hover:underline font-medium">
            {customer.accountNumber}
          </button>
        );
      case "customerName":
        return (
          <button onClick={() => handleViewCustomer(customer)} className="text-primary hover:underline font-medium transition-colors">
            {customer.customerName}
          </button>
        );
      case "currency":
        return customer.currency;
      case "creditLimit": {
        const isBaseCurrencyCustomer = customer.currency === customer.homeCurrencyCode;
        const useBase = currencyMode === "base" || isBaseCurrencyCustomer;
        const limit = useBase ? customer.creditLimit : (customer.currencyCreditLimit ?? customer.creditLimit);
        const displayCurrency = useBase ? (customer.homeCurrencyCode ?? baseCurrency) : customer.currency;
        return <span className="font-medium">{formatCurrency(limit, displayCurrency)}</span>;
      }
      case "totalOutstanding": {
        const outstanding = pickOutstanding(customer);
        const displayCurrency = currencyMode === "base" ? (customer.homeCurrencyCode ?? baseCurrency) : customer.currency;
        return <span className="font-bold">{formatCurrency(outstanding, displayCurrency)}</span>;
      }
      case "terms":
        return customer.terms || "-";
      case "riskLevel":
        return (
          <Badge className={getRiskBadgeColor(customer.riskLevel)}>
            {customer.riskLevel}
          </Badge>
        );
      case "collectionStrategy":
        return customer.collectionStrategy || "-";
      case "lastPayment":
        return customer.lastPayment
          ? new Date(customer.lastPayment).toLocaleDateString("en-GB")
          : "-";
      case "contactEmail":
        return customer.contact || "-";
      case "contactPhone":
        return customer.phone || "-";
      case "creditController":
        return customer.creditControllerName || "-";
      case "accountManager":
        return customer.accountManagerName || "-";
      case "userSortKey1":
        return customer.userSortKey1 || "-";
      case "userSortKey2":
        return customer.userSortKey2 || "-";
      case "userSortKey3":
        return customer.userSortKey3 || "-";
      case "userSortKey4":
        return customer.userSortKey4 || "-";
      default:
        return "-";
    }
  }, [currencyMode, baseCurrency, formatCurrency, pickBandData, pickOutstanding]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-none border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">Ageing Analysis</h1>
          </div>
          <div className="flex items-center gap-2">
            <CurrencyToggle value={currencyMode} onChange={setCurrencyMode} baseLabel={`Base Currency (${baseCurrency})`} />
            <HelpSupportButton screenId="ageing-analysis" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="p-6 pb-0 space-y-6">
          {/* Dynamic Ageing Bucket Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {bands.map((band) => (
              <Card key={band.id} className="border-l-4" style={{ borderLeftColor: band.color || '#6b7280' }}>
                <CardContent className="py-4 px-5">
                  <div className="flex items-center gap-1 mb-2">
                    <p className="text-sm font-medium text-muted-foreground leading-none">
                      {band.bandLabel}
                    </p>
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" aria-label="Metric info" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px] text-center">
                          {`Total shown in ${kpiCurrency}.${activeFilterCount > 0 || searchQuery ? ' Reflects active filters.' : ' Includes all customer accounts.'}`}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-2xl font-bold">
                    {formatCurrency(bandMetrics[band.sequence] ?? 0, kpiCurrency)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Unified toolbar + table panel */}
        <div className="flex flex-col flex-1 min-h-0 px-6 pb-6 mt-6">
          {/* Sticky toolbar */}
          <div className="sticky top-0 z-20 bg-background border rounded-t-xl px-4 py-3">
            <div className="flex items-center gap-4">
              {/* Title + count */}
              <span style={{ fontSize: '18px', fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>
                {(searchQuery || activeFilterCount > 0) ? 'Filtered' : 'All'} Customers ({totalRecords})
              </span>

              {/* Search Bar */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by customer name, account number, or contact"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => { setSearchQuery(""); setCurrentPage(1); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Action Buttons */}
              <Button
                variant="outline"
                onClick={() => setFiltersSheetOpen(true)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Download className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport("CSV")}>
                    Export as CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setColumnConfigOpen(true)}
                      className="h-8 w-8"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Configure columns</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Active Filters row */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-sm text-muted-foreground">Active filters:</span>

                {filters.customerIds.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    Customer: {filters.customerIds.length} selected
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("customer")} />
                  </Badge>
                )}

                {filters.riskLevel.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    Risk: {filters.riskLevel.join(", ")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("riskLevel")} />
                  </Badge>
                )}

                {filters.currency.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    Currency: {filters.currency.join(", ")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("currency")} />
                  </Badge>
                )}

                {(filters.daysOverdueMin !== null || filters.daysOverdueMax !== null) && (
                  <Badge variant="secondary" className="gap-1">
                    Days Overdue: {filters.daysOverdueMin || 0}-{filters.daysOverdueMax || "180+"}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("daysOverdue")} />
                  </Badge>
                )}

                {(filters.outstandingMin !== null || filters.outstandingMax !== null) && (
                  <Badge variant="secondary" className="gap-1">
                    Outstanding: {formatCurrency(filters.outstandingMin || 0)}-{formatCurrency(filters.outstandingMax || 500000)}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("outstanding")} />
                  </Badge>
                )}

                {filters.terms.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    Terms: {filters.terms.join(", ")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("terms")} />
                  </Badge>
                )}

                {filters.creditController.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    Credit Controller: {filters.creditController.join(", ")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("creditController")} />
                  </Badge>
                )}

                {filters.accountManager.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    Account Manager: {filters.accountManager.join(", ")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("accountManager")} />
                  </Badge>
                )}

                {(filters.creditLimitMin !== null || filters.creditLimitMax !== null) && (
                  <Badge variant="secondary" className="gap-1">
                    Credit Limit: {formatCurrency(filters.creditLimitMin ?? 0)}-{formatCurrency(filters.creditLimitMax ?? 1000000)}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("creditLimit")} />
                  </Badge>
                )}

                {(filters.lastPaymentFrom !== null || filters.lastPaymentTo !== null) && (
                  <Badge variant="secondary" className="gap-1">
                    Last Payment: {filters.lastPaymentFrom ?? "any"} - {filters.lastPaymentTo ?? "any"}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("lastPayment")} />
                  </Badge>
                )}

                {filters.collectionStrategy.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    Strategy: {filters.collectionStrategy.join(", ")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("strategy")} />
                  </Badge>
                )}

                {filters.userSortKey1.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    {userSortKeyLabelMap.userSortKey1}: {filters.userSortKey1.join(", ")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("userSortKey1")} />
                  </Badge>
                )}

                {filters.userSortKey2.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    {userSortKeyLabelMap.userSortKey2}: {filters.userSortKey2.join(", ")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("userSortKey2")} />
                  </Badge>
                )}

                {filters.userSortKey3.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    {userSortKeyLabelMap.userSortKey3}: {filters.userSortKey3.join(", ")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("userSortKey3")} />
                  </Badge>
                )}

                {filters.userSortKey4.length > 0 && (
                  <Badge variant="secondary" className="gap-1">
                    {userSortKeyLabelMap.userSortKey4}: {filters.userSortKey4.join(", ")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => removeFilter("userSortKey4")} />
                  </Badge>
                )}

                <Button variant="ghost" size="sm" onClick={resetFilters} className="h-6 text-xs">
                  Clear All
                </Button>
              </div>
            )}
          </div>

          {/* Table card */}
          <Card className="rounded-t-none border-t-0 flex flex-col flex-1 min-h-0">
            <CardContent className="p-0 flex flex-col flex-1 min-h-0">
              <DataTable
                columns={tableColumns}
                data={paginatedCustomers}
                getRowId={(c) => c.id}
                renderCell={renderCell}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                isLoading={isLoading}
                loadingContent={
                  <div className="flex flex-col items-center justify-center space-y-4 py-16">
                    <p className="text-sm text-muted-foreground">Loading ageing analysis...</p>
                  </div>
                }
                emptyContent={
                  <div className="flex flex-col items-center justify-center gap-2 py-8">
                    <p className="text-base font-medium text-muted-foreground">No customers found</p>
                    <p className="text-sm text-muted-foreground">
                      {searchQuery || activeFilterCount > 0
                        ? "Try adjusting your search or filters"
                        : "No ageing analysis data available"}
                    </p>
                    {(searchQuery || activeFilterCount > 0) && (
                      <Button variant="outline" size="sm" onClick={resetFilters} className="mt-2">
                        Clear All Filters
                      </Button>
                    )}
                  </div>
                }
                pagination={{
                  currentPage,
                  totalPages,
                  pageSize,
                  totalItems: totalRecords,
                  onPageChange: handlePageChange,
                  onPageSizeChange: handlePageSizeChange,
                }}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Advanced Filters Sheet */}
      <AdvancedFiltersSheet
        open={filtersSheetOpen}
        onOpenChange={setFiltersSheetOpen}
        filters={tempFilters}
        onFiltersChange={setTempFilters}
        onApply={applyFilters}
        onReset={resetFilters}
        customers={customers}
        categoryKeyLabels={userSortKeyLabelMap}
      />

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columnsWithLabels}
        onApply={savePreferences}
        onReset={resetToDefaults}
      />

      {/* Customer Details Drawer */}
      {selectedCustomer && smartRecommendation && (
        <CustomerDetailsDrawer
          open={customerDetailsOpen}
          onOpenChange={setCustomerDetailsOpen}
          customer={selectedCustomer}
          recommendation={smartRecommendation}
          onExecuteAction={handleExecuteAction}
          onLogCall={() => {
            setActiveCustomer(selectedCustomer);
            setLogCallOpen(true);
          }}
          onSendEmail={() => {
            setActiveCustomer(selectedCustomer);
            setSendEmailOpen(true);
          }}
          onViewFullProfile={() => {
            setCustomerDetailsOpen(false);
            navigate(`/customers/${selectedCustomer.customerId ?? selectedCustomer.id}`);
          }}
          formatCurrency={formatCurrency}
          getPriorityBadgeColor={getPriorityBadgeColor}
          getActionIcon={getActionIcon}
          getActionButtonText={getActionButtonText}
          currencyMode={currencyMode}
        />
      )}

      {/* Smart Actions Drawer (Bulk Selection) */}
      {smartRecommendation && (
        <SmartActionsDrawer
          open={smartActionsOpen}
          onOpenChange={setSmartActionsOpen}
          recommendation={smartRecommendation}
          onExecuteAction={handleExecuteAction}
          formatCurrency={formatCurrency}
          getPriorityBadgeColor={getPriorityBadgeColor}
          getActionIcon={getActionIcon}
          getActionButtonText={getActionButtonText}
        />
      )}

      {/* Action Dialogs */}
      <LogCallDialog
        open={logCallOpen}
        onOpenChange={setLogCallOpen}
        customer={activeCustomer}
      />

      <SendEmailDialog
        open={sendEmailOpen}
        onOpenChange={setSendEmailOpen}
        customer={activeCustomer}
      />

      <CreatePromiseDialog
        open={createPromiseOpen}
        onOpenChange={setCreatePromiseOpen}
        customer={activeCustomer}
      />

      <AssignClerkDialog
        open={assignClerkOpen}
        onOpenChange={setAssignClerkOpen}
        customer={activeCustomer}
      />
    </div>
  );
}
